import { Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import usePoll from '../../../hooks/usePoll';
import { RoundOptions } from '../../../services/backendService/types';
import { getIndexToColor } from '../../../utils/roundUtils';
import { isCuripodText, parseStringToSlateObject } from '../../SlideComponents/slateUtil';
import { CenteredContainer } from '../../styled/CenteredContainer';

const Poll = ({
  roundId,
  gameId,
  staticData,
  isAuthenticated,
  hideCount = false,
}: {
  roundId?: string;
  gameId?: string;
  isAuthenticated: boolean;
  staticData?: RoundOptions[] | null;
  hideCount?: boolean;
}) => {
  const { data } = usePoll({ roundId, gameId, isAuthenticated });

  const [presentableData, setPresentableData] = useState<RoundOptions[]>();
  const fontColor = useMemo(() => {
    if (presentableData && !!presentableData?.[0]) {
      // using first option color as display color for count
      // We want the outermost leaf node color to take presedence, and then fallback to the paragraph
      // element. Then fallback to the default theme color.
      const obj = parseStringToSlateObject(presentableData[0].displayName);
      const firstElement = obj.find(e => e.type === 'paragraph');
      const paragraphColor = firstElement?.color ?? theme.colors.black;

      const firstChild = firstElement?.children?.[0];
      // Using the leaf node color if it exists - otherwise use the paragraph color.
      const color = firstChild
        ? isCuripodText(firstChild)
          ? firstChild?.color ?? paragraphColor
          : paragraphColor
        : paragraphColor;
      return color;
    }

    //Default to black if no data is present
    return theme.colors.black;
  }, [presentableData]);
  useEffect(() => {
    if (data) {
      setPresentableData(data);
    }
  }, [data]);

  useEffect(() => {
    if (staticData) {
      setPresentableData(staticData);
    }
  }, [staticData]);
  if (!presentableData)
    return (
      <CenteredContainer style={{ height: '90%', width: `100%` }}>
        <Spin />
      </CenteredContainer>
    );
  if (presentableData.length == 0) return null;

  return (
    <>
      <Container width="100%" height="100%">
        <BarChart
          width={150}
          id={'poll-bar-chart'}
          data={presentableData}
          margin={{ top: 0, right: 10, left: 10, bottom: 10 }}
        >
          <Bar
            dataKey="count"
            shape={<CuripodBar data={presentableData} />}
            minPointSize={5}
            label={
              hideCount
                ? undefined
                : {
                    position: 'top',
                    fontWeight: 800,
                    fill: fontColor,
                    fontSize: `${(1920 * 0.1) / presentableData.length}px`,
                  }
            }
          />
        </BarChart>
      </Container>
    </>
  );
};
const Container = styled(ResponsiveContainer)`
  svg {
    overflow: visible;
  }
  tspan {
    font-size: 100%;
    cursor: default;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CuripodBar = (props: any) => {
  const { index, width, height, x, y, data } = props;
  const roundOption = data[index];
  const fill = roundOption?.color || getIndexToColor(index);
  return (
    <rect
      x={x}
      y={y}
      width={width >= 0 ? width : 0}
      height={height >= 0 ? height : 0}
      rx="10"
      fill={fill}
      filter={'drop-shadow(rgba(0, 0, 0, 0.1) 0px -2px 3px)'}
    />
  );
};

export default Poll;
